import { STORAGE_NAMESPACE } from "../config";

/**
 * Who the page's events and feedback belong to (B3.10). The rooms keep knowing a visitor by the
 * tab's guest ID (`tabGuestId`, B3.7): two tabs of one browser are two players (D-054), so the
 * identity here never reaches a room.
 *
 * - "tab": the tab's random ID, gone when the tab closes; where the kept guest's switch is off.
 * - "kept": a random ID kept in the browser's storage across visits, so a return can be counted
 *   (the plan's "D1 return"). It is a switch (`VITE_KEPT_GUEST`, `?keptguest=on`), on since the
 *   owner approved the note that describes it (N35, D-071).
 * - "google": signed in with Google on our own URL (B3.10.1, D-077, `src/front/signIn.ts`): the
 *   account's stable ID the Worker made from Google's ID token (D-078), kept in the browser's
 *   storage until Sign out, takes the guest's place here; nothing else in the page changes. A
 *   switch (`VITE_SIGNIN`, `?signin=on`), off for visitors until the owner approves the note that
 *   describes it (N38): with the switch off a kept account is not read.
 */
export type IdentityKind = "tab" | "kept" | "google";

export interface Identity {
  readonly kind: IdentityKind;
  readonly id: string;
}

/** A store of the browser: the lasting one (`PlatformAdapter.data`), or the tab's. */
export interface KeptStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

/** The stored value of `key`, or a new one, stored at once; a store that throws keeps none. */
export function keptOrMade(store: KeptStore, key: string, fresh: () => string): string {
  try {
    const kept = store.get(key);
    if (kept) {
      return kept;
    }
    const made = fresh();
    store.set(key, made);
    return made;
  } catch {
    return fresh();
  }
}

/** The kept guest's key: not the tab's (`gf:guest` in `sessionStorage`), never the save's. */
export const KEPT_GUEST_KEY = `${STORAGE_NAMESPACE}:keptGuest`;

/** The kept guest's ID: the stored one, or a new one, stored at once; a store that throws keeps none. */
export function keptGuestId(store: KeptStore, fresh: () => string): string {
  return keptOrMade(store, KEPT_GUEST_KEY, fresh);
}

/** The signed-in account's key in the lasting store (B3.10.1): the Worker's ID, never Google's. */
export const ACCOUNT_KEY = `${STORAGE_NAMESPACE}:account`;

/** The page's identity: the signed-in account, else the kept guest where its switch is on, else the tab's. */
export function identityFor(options: {
  readonly kept: boolean;
  readonly tabId: string;
  readonly store: KeptStore;
  readonly fresh: () => string;
  /** The account's ID where sign-in is on and the visitor signed in; undefined otherwise. */
  readonly account?: string | undefined;
}): Identity {
  if (options.account) {
    return { kind: "google", id: options.account };
  }
  return options.kept
    ? { kind: "kept", id: keptGuestId(options.store, options.fresh) }
    : { kind: "tab", id: options.tabId };
}
